import clsx from 'clsx';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useCalendarTypes } from '../../hooks/useCalendarTypes';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { useToast } from '../../hooks/useToast';
import { isHostedByTeamHub } from '../../services/hostAuthService';
import { settingsService } from '../../services/settingsService';
import { getUiTracer } from '../../telemetry/otelClient';
import type { EventFilter, UserInfo } from '../../types';
import { CalendarRecentPanel } from '../CalendarRecentPanel';
//import { DownloadAppsSection } from '../DownloadAppsSection';
import EventTab from '../EventTab/EventTab';
import { Footer } from '../Footer';
import { HomeControlsPanel } from '../HomeControlsPanel';
import { ToastContainer } from '../Toast';

interface AuthenticatedHomePageProps {
  user: UserInfo;
  onNewMeeting?: () => void;
  isNewMeetingPending?: boolean;
  onJoinMeeting?: () => void;
  onScheduleMeeting?: () => void;
  /**
   * Personal Meetings entry-point. SM-1273: only surfaced in MF
   * (TeamHub-hosted) mode, as a link above the action tiles — standalone
   * reaches it from the global header instead.
   */
  onPersonalMeetings?: () => void;
  /**
   * Settings entry-point for the hosted (MF) HomeControlsPanel tile.
   * Not currently passed by HomePage — SM-1165 removed the Settings tile
   * from MF mode since TeamHub's own chrome already provides it. Left as an
   * optional prop in case it needs to come back.
   */
  onSettings?: () => void;
}

const AuthenticatedHomePage: React.FC<AuthenticatedHomePageProps> = ({
  user,
  onNewMeeting,
  isNewMeetingPending,
  onJoinMeeting,
  onScheduleMeeting,
  onPersonalMeetings,
  onSettings,
}) => {
  const [activeTab, setActiveTab] = useState('upcoming');
  const [searchInput, setSearchInput] = useState('');
  const [selectEventFilter, setSelectEventFilter] = useState<EventFilter>('all');
  const [isEventTabVisible, setIsEventTabVisible] = useState<boolean>(() => {
    const cachedCalendar = settingsService.getSettings().calendar;
    return cachedCalendar.google.connected || cachedCalendar.microsoft.connected;
  });
  const { isFetching: isCalendarLoading, isError: isCalendarError } = useCalendarTypes();
  // Toast management
  const { toasts, removeToast, showToast } = useToast();

  const tracer = getUiTracer();

  // Context and hooks
  const { handleComponentError } = useErrorHandler('AuthenticatedHomePage');

  // Header action handlers - integrate with existing meeting systems
  const handleNewMeeting = () => {
    const span = tracer.startSpan('ui.click.new_meeting_button', {
      attributes: {
        'ui.component': 'AuthenticatedHomePage',
        'ui.action': 'new_meeting',
        'ui.button.label': 'New meeting',
      },
    });

    try {
      console.log('Starting new meeting...');

      if (onNewMeeting) {
        // Use parent-provided handler if available
        onNewMeeting();
      } else {
        // Default behavior: generate unique room and trigger meeting creation
        const uniqueRoomId = `meet-${crypto.randomUUID()}`;
        console.log('Generated room ID:', uniqueRoomId);

        // This would typically set the room state in the parent App component
        // For now, we'll dispatch a custom event that the App component can listen to
        window.dispatchEvent(
          new CustomEvent('start-new-meeting', {
            detail: { roomId: uniqueRoomId },
          }),
        );
      }
    } catch (error) {
      span.recordException(error as Error);
      span.setStatus({ code: 2, message: 'Failed to start new meeting' });
      handleComponentError(
        error instanceof Error ? error : new Error('Failed to start new meeting'),
        'start_new_meeting',
      );
    } finally {
      span.end();
    }
  };

  const handleJoinMeeting = () => {
    try {
      console.log('Opening join meeting interface...');

      if (onJoinMeeting) {
        // Use parent-provided handler if available
        onJoinMeeting();
      } else {
        // Default behavior: prompt for room name and join
        const roomName = prompt('Enter meeting room name or ID:');
        if (roomName?.trim()) {
          console.log('Joining room:', roomName.trim());

          // Dispatch event for App component to handle
          window.dispatchEvent(
            new CustomEvent('join-meeting', {
              detail: { roomName: roomName.trim() },
            }),
          );
        }
      }
    } catch (error) {
      handleComponentError(
        error instanceof Error ? error : new Error('Failed to join meeting'),
        'join_meeting',
      );
    }
  };

  // In TeamHub-hosted (MF) mode the global NG Meet header is hidden, so the
  // Personal Meetings entry-point is surfaced as a link above the action
  // tiles instead (SM-1273). `isHosted` also gates the Settings tile and the
  // footer, both of which TeamHub's own chrome already provides.
  const isHosted = useMemo(() => isHostedByTeamHub(), []);

  const handleProvider = useCallback((provider: boolean) => {
    setIsEventTabVisible(provider);
  }, []);

  const showCalendarErrorToast = useCallback(
    (title: string, message: string) => {
      showToast({
        type: 'error',
        title,
        message,
        duration: 7000,
      });
    },
    [showToast],
  );

  const handleCalendarUpdateMeetingError = useCallback(
    (message: string) => {
      showCalendarErrorToast('Unable to Update Meeting', message);
    },
    [showCalendarErrorToast],
  );

  const handleCalendarFetchAuthError = useCallback(
    (message: string) => {
      showCalendarErrorToast('Calendar Connection Expired', message);
    },
    [showCalendarErrorToast],
  );

  // Main render logic - always render CalendarRecentPanel with transition support
  const renderMainContent = () => {
    return (
      <div className="calendar-panel-container">
        <CalendarRecentPanel
          userInfo={user}
          activeTab={activeTab}
          searchInput={searchInput}
          filterInput={selectEventFilter}
          onEventsProvider={handleProvider}
          onCalendarUpdateMeetingError={handleCalendarUpdateMeetingError}
          onCalendarFetchAuthError={handleCalendarFetchAuthError}
        />
      </div>
    );
  };

  return (
    <div className="authenticated-home-page">
      {/* Skip to main content link for screen readers */}
      <a href="#main-content" className="skip-to-main">
        Skip to main content
      </a>

      {/* Home content with consistent layout structure */}
      <div className="authenticated-home-content">
        <div
          className={clsx('event-top-area', !isEventTabVisible && 'event-top-area--disconnected')}
        >
          <div
            className={clsx(
              'event-tab-container',
              !isEventTabVisible && 'event-tab-container--hidden',
            )}
          >
            {isEventTabVisible && (
              <div className="event-tab-stack">
                <div
                  className={clsx(
                    'event-tab-stack__item',
                    isCalendarLoading && 'event-tab-stack__item--visible',
                  )}
                  aria-hidden={!isCalendarLoading}
                >
                  <div className="event-tab-skeleton">
                    <div className="skeleton event-tab-skeleton__bar" />
                    <div className="skeleton event-tab-skeleton__bar" />
                    <div className="skeleton event-tab-skeleton__bar" />
                  </div>
                </div>
                <div
                  className={clsx(
                    'event-tab-stack__item',
                    !(isCalendarLoading || isCalendarError) && 'event-tab-stack__item--visible',
                  )}
                  aria-hidden={isCalendarLoading || isCalendarError}
                >
                  <EventTab
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    onSearchInput={setSearchInput}
                    onSelectEventFilter={setSelectEventFilter}
                  />
                </div>
              </div>
            )}
            <HomeControlsPanel
              onNewMeeting={handleNewMeeting}
              isNewMeetingPending={isNewMeetingPending}
              onJoinMeeting={handleJoinMeeting}
              onScheduleMeeting={onScheduleMeeting}
              onPersonalMeetings={isHosted ? onPersonalMeetings : undefined}
              onSettings={isHosted ? onSettings : undefined}
            />
          </div>
        </div>

        {/* Main calendar content - with smooth transitions */}
        <div className="authenticated-main-section">
          <main
            id="main-content"
            className="main-content"
            aria-label="Authenticated home page content"
          >
            <div className="calendar-content-wrapper">{renderMainContent()}</div>
          </main>
        </div>

        {/* Download section - consistent across all states
        <div className="authenticated-download-section">
          <DownloadAppsSection />
        </div>
        */}

        {/* Footer is only shown in standalone mode; TeamHub already provides its own chrome */}
        {!isHosted && (
          <div className="authenticated-footer-section">
            <Footer />
          </div>
        )}
      </div>

      {/* Toast notifications */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
};

export default AuthenticatedHomePage;
